import React, { createContext, useContext, useState, ReactNode } from "react";
import { BACKEND_URL } from "../constants/urls.const";

type AnalysisState = {
  text: string;
  setText: (v: string) => void;
  file: File | null;
  setFile: (f: File | null) => void;
  loading: boolean;
  result: any;
  error: string;
  jobId: string | null;
  handleClear: () => void;
  handleSubmit: () => Promise<void>;
};

const AnalysisContext = createContext<AnalysisState | undefined>(undefined);

export const AnalysisProvider = ({ children }: { children: ReactNode }) => {
  const [text, setText] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState("");
  const [jobId, setJobId] = useState<string | null>(null);

  // Polling function to get job status
  const pollJobStatus = async (id: string) => {
    try {
      const response = await fetch(`${BACKEND_URL}/analyze/${id}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();

      const status = data.status as Record<string, string> | undefined;
      const allDone = status
        ? Object.values(status).every((s) => s === "done")
        : false;

      setResult(data);

      if (allDone) {
        // All workers finished — stop loading
        setLoading(false);
      } else {
        // keep polling until allDone
        setTimeout(() => pollJobStatus(id), 1000);
      }
    } catch (err) {
      console.error(err);
      setError(
        err instanceof Error ? err.message : "Error fetching job status"
      );
      setLoading(false);
    }
  };

  const handleClear = () => {
    setText("");
    setFile(null);
    setLoading(false);
    setResult(null);
    const fileInput = document.getElementById("pdf-upload");
    if (fileInput instanceof HTMLInputElement) {
      fileInput.value = "";
    }
  };

  const handleSubmit = async () => {
    // mark loading and clear previous state
    setLoading(true);
    setError("");
    setResult(null);
    setJobId(null);

    try {
      const formData = new FormData();
      
      if (file) {
        formData.append("file", file);
      } else if (text) {
        formData.append("text", text);
      } else {
        throw new Error("Please provide either text or a PDF file");
      }

      const response = await fetch(`${BACKEND_URL}/analyze/`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setJobId(data.job_id);

      // start polling — loading will be cleared by pollJobStatus once allDone
      pollJobStatus(data.job_id);
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : "An error occurred");
      setLoading(false);
    }
  };

  return (
    <AnalysisContext.Provider
      value={{ text, setText, file, setFile, loading, result, error, jobId, handleClear, handleSubmit }}
    >
      {children}
    </AnalysisContext.Provider>
  );
};

export const useAnalysis = () => {
  const ctx = useContext(AnalysisContext);
  if (!ctx) throw new Error("useAnalysis must be used within AnalysisProvider");
  return ctx;
};

export default AnalysisContext;
